// ===== CART PACKAGING =====
class CartPackaging extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.isUpdating = false;
    this.updateTimeout = null;
    this.hasListener = false;
  }

  connectedCallback() {
    // Nettoyer tous les autres listeners existants pour éviter la duplication
    const existingElements = document.querySelectorAll("cart-packaging");
    existingElements.forEach((el) => {
      if (el !== this && el.hasListener) {
        el.removeEventListener("change", el.handleChange);
        el.hasListener = false;
      }
    });

    // Ajouter le listener seulement si pas déjà présent
    if (!this.hasListener) {
      this.addEventListener("change", this.handleChange);
      this.hasListener = true;
    }
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);
    this.hasListener = false;
    // Nettoyer le timeout en cours
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  handleChange(event) {
    if (event.target.matches('input[name="attributes[emballage]"]')) {
      // Mettre à jour l'état visuel
      this.updateRadioStates(event.target);

      // Mettre à jour le panier
      this.updateCart(event.target.value);
    }
  }

  updateRadioStates(selectedRadio) {
    const allRadios = this.querySelectorAll(
      'input[name="attributes[emballage]"]'
    );
    allRadios.forEach((radio) => {
      radio.checked = radio === selectedRadio;
      if (radio === selectedRadio) {
        radio.setAttribute("checked", "");
      } else {
        radio.removeAttribute("checked");
      }
    });
  }

  async updateCart(value) {
    // Éviter les requêtes simultanées
    if (this.isUpdating) return;

    // Annuler la requête précédente si elle existe
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(async () => {
      this.isUpdating = true;

      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            attributes: {
              emballage: value,
            },
          }),
        });

        const data = await response.json();
        console.log("Emballage mis à jour:", value);
      } catch (error) {
        console.error("Erreur:", error);
      } finally {
        this.isUpdating = false;
      }
    }, 300); // 300ms de délai pour éviter les appels multiples
  }
}

// Nettoyer les anciennes instances avant de définir le composant
const existingElements = document.querySelectorAll("cart-packaging");
existingElements.forEach((el) => {
  if (el.handleChange) {
    el.removeEventListener("change", el.handleChange);
  }
});

// Enregistrer le composant seulement s'il n'existe pas déjà
if (!customElements.get("cart-packaging")) {
  customElements.define("cart-packaging", CartPackaging);
}

// ===== CART SAMPLES AUTO-REMOVAL =====
class CartSamplesManager {
  constructor() {
    this.threshold = null;
    this.init();
  }

  async init() {
    // Get the threshold from Shopify settings
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Get threshold from theme settings via a separate request
      this.threshold = await this.getThresholdFromSettings();
      
      if (this.threshold) {
        this.checkAndRemoveSamples(cart);
      }
    } catch (error) {
      // Silently handle initialization errors
    }
  }

  async getThresholdFromSettings() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.dataset.samplesThreshold) {
      const threshold = parseFloat(cartDrawer.dataset.samplesThreshold);
      return threshold;
    }
    return null;
  }

  async checkAndRemoveSamples(cart) {
    if (!this.threshold) {
      return;
    }

    const thresholdCents = this.threshold * 100;
    
    // Filter items by product title containing "2.5ml"
    const samplesInCart = cart.items.filter(item => {
      const title = item.product_title || item.title || '';
      return title.toLowerCase().includes('2.5ml');
    });

    if (cart.total_price < thresholdCents && samplesInCart.length > 0) {
      await this.removeSamplesFromCart(samplesInCart);
    }
  }

  async removeSamplesFromCart(sampleItems) {
    try {
      // Prepare sections to bundle like the theme does
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const updates = {};
      sampleItems.forEach(item => {
        updates[item.key] = 0; // Set quantity to 0 to remove
      });

      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ 
          updates,
          sections: sectionsToBundle.join(",")
        })
      });
      
      if (response.ok) {
        const responseJson = await response.json();
        
        // Use the theme's cart system to dispatch the change event
        document.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            cart: responseJson,
            baseEvent: 'sample:remove'
          }
        }));
      }
    } catch (error) {
      // Silently handle errors
    }
  }
}

// Initialize the samples manager
const samplesManager = new CartSamplesManager();

// Listen to cart changes to check and remove samples
document.addEventListener('cart:change', (event) => {
  if (event.detail?.baseEvent !== 'sample:remove') { // Avoid infinite loop
    samplesManager.checkAndRemoveSamples(event.detail.cart);
  }
});

// Also check when cart is refreshed
document.addEventListener('cart:refresh', async () => {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    samplesManager.checkAndRemoveSamples(cart);
  } catch (error) {
    // Silently handle errors
  }
});

// ===== CART DRAWER REASSURANCE SWIPER =====
if (
  typeof Swiper !== "undefined" &&
  document.querySelector("#reassurance-swiper")
) {
  const cartDrawerReassuranceSwiper = new Swiper("#reassurance-swiper", {
    autoplay: {
      delay: 3000, //3sec
      disableOnInteraction: false,
    },
    loop: true,
    slidesPerView: 1,
    spaceBetween: 0,
    effect: "slide",
    pagination: {
      el: ".reassurance-swiper-pagination",
      clickable: true,
    },
  });
}
